import React, { useState } from "react";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import Switch from "react-switch";
import PageTitle from "../../layouts/PageTitle";
import {
  addLabourTypeApi,
  updateLabourTypeApi,
  updateLabourTypeStatusApi,
} from "../../../services/labour";
import { appApi, useGetLabourTypesQuery } from "../../../store/appApi";

// ─── default shapes ───────────────────────────────────────────────────────────
const EMPTY_ADD_FORM = { name: "", laborCat: "", typeOfLabour: "", rate: "" };
const EMPTY_EDIT_FORM = {
  _id: "",
  name: "",
  laborCat: "",
  typeOfLabour: "",
  rate: "",
};

const AddType = () => {
  const dispatch = useDispatch();

  // ── Add form state ────────────────────────────────────────────────────────
  const [form, setForm] = useState(EMPTY_ADD_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // ── Remote data ───────────────────────────────────────────────────────────
  const {
    data: catData,
    isLoading: catLoading,
    isFetching: catFetching,
  } = useGetLabourTypesQuery();

  // API returns { success, labourtype } from getLabourTypes
  // or { success, list } from getLabourTypeList — handle both
  const categories = Array.isArray(catData?.labourtype)
    ? catData.labourtype
    : Array.isArray(catData?.list)
      ? catData.list
      : [];

  const tableLoading = catLoading || catFetching;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  // ── Add form handlers ─────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateAdd = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Name is required.";
    if (!form.laborCat?.trim()) e.laborCat = "Labour category is required.";
    if (!form.typeOfLabour) e.typeOfLabour = "Type is required.";
    if (!form.rate?.trim()) e.rate = "Rate is required.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateAdd();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await addLabourTypeApi({
        name: form.name.trim(),
        laborCat: form.laborCat.trim(),
        typeOfLabour: form.typeOfLabour,
        rate: form.rate.trim(),
      });

      dispatch(appApi.util.invalidateTags(["LabourTypes"]));
      setForm(EMPTY_ADD_FORM);
      setErrors({});

      Swal.fire({
        icon: "success",
        title: "Labour Type Created",
        text: `"${form.laborCat.trim()}" has been added.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to create labour type. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  // ── Edit modal handlers ───────────────────────────────────────────────────
  const openEdit = (cat) => {
    setEditForm({
      _id: cat._id,
      name: cat.name || "",
      laborCat: cat.laborCat || "",
      typeOfLabour: cat.typeOfLabour || "",
      rate: cat.rate || "",
    });
    setEditErrors({});
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.name?.trim()) e.name = "Name is required.";
    if (!editForm.laborCat?.trim()) e.laborCat = "Labour category is required.";
    return e;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const validationErrors = validateEdit();
    if (Object.keys(validationErrors).length) {
      setEditErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      await updateLabourTypeApi(editForm._id, {
        name: editForm.name.trim(),
        laborCat: editForm.laborCat.trim(),
        typeOfLabour: editForm.typeOfLabour,
        rate: editForm.rate.trim(),
      });

      dispatch(appApi.util.invalidateTags(["LabourTypes"]));
      setEditModal(false);

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Labour type updated successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to update labour type.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  // ── Status toggle ─────────────────────────────────────────────────────────
  const handleStatusToggle = async (cat) => {
    const newStatus = !cat.status;
    try {
      await updateLabourTypeStatusApi(cat._id, newStatus);
      dispatch(appApi.util.invalidateTags(["LabourTypes"]));
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update status. Please try again.",
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <PageTitle activeMenu="Labour Types" motherMenu="Labour" />

      <div className="row g-3">
        {/* ── Add Form ──────────────────────────────────────────────────────── */}
        <div className="col-xl-4 col-lg-5">
          <div className="card" style={{ position: "sticky", top: 0 }}>
            <div className="card-header">
              <h5 className="card-title mb-0">Add New Labour Type</h5>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>
                {/* Name */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="name">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    placeholder="e.g. Ramesh Kumar"
                    value={form.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                {/* Labour Category */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="laborCat">
                    Labour Category <span className="text-danger">*</span>
                  </label>
                  <input
                    id="laborCat"
                    name="laborCat"
                    type="text"
                    className={`form-control ${errors.laborCat ? "is-invalid" : ""}`}
                    placeholder="e.g. Mason, Carpenter, etc."
                    value={form.laborCat}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.laborCat && (
                    <div className="invalid-feedback">{errors.laborCat}</div>
                  )}
                </div>

                {/* Type of Labour */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="typeOfLabour">
                    Type <span className="text-danger">*</span>
                  </label>
                  <select
                    id="typeOfLabour"
                    name="typeOfLabour"
                    className={`form-control ${errors.typeOfLabour ? "is-invalid" : ""}`}
                    value={form.typeOfLabour}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Select type…</option>
                    <option value="daily">Daily</option>
                    <option value="hourly">Hourly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  {errors.typeOfLabour && (
                    <div className="invalid-feedback">
                      {errors.typeOfLabour}
                    </div>
                  )}
                </div>

                {/* Rate */}
                <div className="form-group mb-4">
                  <label className="form-label" htmlFor="rate">
                    Rate (₹) <span className="text-danger">*</span>
                  </label>
                  <input
                    id="rate"
                    name="rate"
                    type="number"
                    min="0"
                    className={`form-control ${errors.rate ? "is-invalid" : ""}`}
                    placeholder="e.g. 700"
                    value={form.rate}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.rate && (
                    <div className="invalid-feedback">{errors.rate}</div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── Labour Types Table ─────────────────────────────────────────────── */}
        <div className="col-xl-8 col-lg-7">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">Existing Labour Types</h5>
              {!tableLoading && (
                <span className="badge badge-primary">{categories.length}</span>
              )}
            </div>

            <div className="card-body" style={{ overflowY: "auto", maxHeight: "calc(100vh - 250px)" }}>
              <div className="table-responsive" style={{ minWidth: 0 }}>
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Rate (₹)</th>
                      <th>Added By</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading && (
                      <tr>
                        <td colSpan={9} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}

                    {!tableLoading && categories.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center py-4 text-muted">
                          No labour types found. Use the form to add one.
                        </td>
                      </tr>
                    )}

                    {!tableLoading &&
                      categories.map((cat, idx) => (
                        <tr key={cat._id || idx}>
                          <td className="text-muted small">{idx + 1}</td>
                          <td className="fw-semibold">{cat.name || "—"}</td>
                          <td className="text-muted small">
                            {cat.laborCat || "—"}
                          </td>
                          <td
                            className="text-muted small"
                            style={{ textTransform: "capitalize" }}
                          >
                            {cat.typeOfLabour || "—"}
                          </td>
                          <td className="text-muted small">
                            {cat.rate ? `₹ ${cat.rate}` : "—"}
                          </td>
                          <td
                            className="text-muted small"
                            style={{ textTransform: "capitalize" }}
                          >
                            {cat.user || "—"}
                          </td>
                          <td className="text-muted small">
                            {formatDate(cat.created)}
                          </td>

                          {/* ── Status toggle ── */}
                          <td>
                            <Switch
                              onChange={() => handleStatusToggle(cat)}
                              checked={cat.status !== false}
                              onColor="#86d3ff"
                              onHandleColor="#2693e6"
                              offColor="#d3d3d3"
                              handleDiameter={18}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              height={22}
                              width={44}
                            />
                          </td>

                          {/* ── Edit button ── */}
                          <td>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-primary"
                              title="Edit"
                              onClick={() => openEdit(cat)}
                            >
                              <i className="fa fa-pencil" />
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Labour Type Modal ─────────────────────────────────────────────── */}
      <Modal show={editModal} onHide={() => setEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Labour Type</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form id="edit-labour-form" onSubmit={handleUpdate} noValidate>
            {/* Name */}
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-name">
                Name <span className="text-danger">*</span>
              </label>
              <input
                id="edit-name"
                name="name"
                type="text"
                className={`form-control ${editErrors.name ? "is-invalid" : ""}`}
                value={editForm.name}
                onChange={handleEditChange}
              />
              {editErrors.name && (
                <div className="invalid-feedback">{editErrors.name}</div>
              )}
            </div>

            {/* Labour Category */}
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-laborCat">
                Labour Category <span className="text-danger">*</span>
              </label>
              <input
                id="edit-laborCat"
                name="laborCat"
                type="text"
                className={`form-control ${editErrors.laborCat ? "is-invalid" : ""}`}
                value={editForm.laborCat}
                onChange={handleEditChange}
              />
              {editErrors.laborCat && (
                <div className="invalid-feedback">{editErrors.laborCat}</div>
              )}
            </div>

            {/* Type */}
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-typeOfLabour">
                Type
              </label>
              <select
                id="edit-typeOfLabour"
                name="typeOfLabour"
                className="form-control"
                value={editForm.typeOfLabour}
                onChange={handleEditChange}
              >
                <option value="">Select type…</option>
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Rate */}
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-rate">
                Rate (₹)
              </label>
              <input
                id="edit-rate"
                name="rate"
                type="number"
                min="0"
                className="form-control"
                value={editForm.rate}
                onChange={handleEditChange}
              />
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="edit-labour-form"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddType;
